// Doctor / admin only — the clinical reference.
//
// A single read-only screen that documents exactly what the dashboard watches
// for and how every number on it is produced. Everything here is rendered from
// the live config (RED_FLAGS, TIER_META, DOMAIN_GROUPS, the scoring bands and
// the attention weights), so it can never drift from what the app actually does.
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle, BookOpen, ChevronLeft, ShieldCheck } from "lucide-react";
import AppShell from "@/components/AppShell";
import { Section, Chip } from "@/components/clinical/kit";
import {
  ATTENTION_WEIGHTS, DOMAIN_GROUPS, DOMAIN_LABEL, MATERIAL_DROP,
  RED_FLAGS, SCORED_WIDS, TIER_META, adherenceTone,
} from "@/config/clinical";
import { getWorksheet } from "@/config/worksheets";
import { interpret } from "@/lib/status";

// Resolve each flag to its worksheet number + title once, then split by tier.
function useFlags() {
  return useMemo(() => {
    const resolved = RED_FLAGS.map((f) => {
      const ws = getWorksheet(f.wid);
      return { ...f, num: ws?.num || "", title: ws?.title || f.wid };
    });
    const byTier = (tier) =>
      resolved
        .filter((f) => f.tier === tier)
        .sort((a, b) => String(a.num).localeCompare(String(b.num), undefined, { numeric: true }));
    return { urgent: byTier("urgent"), caution: byTier("caution") };
  }, []);
}

function FlagTable({ list, tier }) {
  const { t } = useTranslation();
  const m = TIER_META[tier];
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[640px] text-sm">
        <thead>
          <tr className="text-left font-mono text-[11px] uppercase tracking-wider text-stone-500">
            <th className="px-3 py-2 w-24">{t("Worksheet")}</th>
            <th className="px-3 py-2">{t("Symptom ticked")}</th>
            <th className="px-3 py-2">{t("Action")}</th>
          </tr>
        </thead>
        <tbody>
          {list.map((f) => (
            <tr key={`${f.wid}-${f.label}`} className="border-t border-[#EFE9DE] align-top"
              data-testid={`ref-flag-${f.wid}-${f.label.replace(/\s+/g, "-")}`}>
              <td className="px-3 py-2 whitespace-nowrap">
                <span className="font-mono text-xs font-semibold text-stone-700">{t("W")}{f.num}</span>
                <div className="text-[11px] text-stone-400">{t(f.title)}</div>
              </td>
              <td className={`px-3 py-2 font-semibold ${m.text}`}>{t(f.label)}</td>
              <td className="px-3 py-2 text-stone-700">{t(f.action)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// A live swatch of one interpretation band, coloured by interpret() itself.
function Band({ score, range }) {
  const it = interpret(score);
  return (
    <div className="flex items-center gap-2 rounded-md border border-[#E5DFD3] bg-white px-3 py-2">
      <span className="h-3 w-3 flex-shrink-0 rounded-full" style={{ backgroundColor: it.color }} />
      <span className="font-mono text-xs font-semibold tabular-nums text-stone-600">{range}</span>
      <span className="text-sm" style={{ color: it.color }}>{it.label}</span>
    </div>
  );
}

export default function ClinicalReferencePage() {
  const nav = useNavigate();
  const { t } = useTranslation();
  const { urgent, caution } = useFlags();
  const W = ATTENTION_WEIGHTS;

  const weightRows = [
    { label: "Each urgent finding", value: `× ${W.urgent}`, note: "Any urgent finding floats the participant to the top." },
    { label: "Each 'discuss today' finding", value: `× ${W.caution}`, note: "Added after urgent items." },
    { label: "A falling overall score", value: `× ${W.fallingTrend}`, note: "Per point dropped since the previous stage." },
    { label: "A low overall score", value: `× ${W.lowScore}`, note: "Applied to (10 − overall), so lower scores rank higher." },
    { label: "No score yet", value: `+ ${W.noScore}`, note: "Flat weight when the current stage has no overall score." },
    { label: "Days since last update", value: `× ${W.staleness}`, note: `Capped at ${W.staleCap} days, so a stale record cannot outrank a finding.` },
  ];

  const adhBands = [
    { v: 2, range: "0–3.9" }, { v: 5, range: "4–6.9" }, { v: 8, range: "7–10" },
  ].map((b) => ({ ...b, tone: adherenceTone(b.v) }));

  return (
    <AppShell>
      <div className="mb-5">
        <button onClick={() => nav(-1)} data-testid="ref-back"
          className="mb-1 flex items-center gap-1 text-xs text-stone-500 hover:text-emerald-700">
          <ChevronLeft className="h-3 w-3" /> {t("Back")}
        </button>
        <h1 className="flex items-center gap-2 font-serif text-3xl font-bold text-stone-900" data-testid="ref-title">
          <BookOpen className="h-6 w-6 text-emerald-800" />
          {t("Clinical reference")}
        </h1>
        <p className="text-sm text-stone-500">
          {t("What the dashboard flags, and how every score is produced. This page is generated from the live configuration.")}
        </p>
      </div>

      {/* summary tiles */}
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { k: "urgent", label: "Urgent flags defined", value: urgent.length, tone: "text-rose-700" },
          { k: "caution", label: "'Discuss today' flags", value: caution.length, tone: "text-amber-700" },
          { k: "total", label: "Flags in total", value: urgent.length + caution.length, tone: "text-stone-800" },
          { k: "domains", label: "Scored domains", value: SCORED_WIDS.length, tone: "text-emerald-800" },
        ].map((s) => (
          <div key={s.k} className="rounded-lg border border-[#E5DFD3] bg-white px-4 py-2.5" data-testid={`ref-stat-${s.k}`}>
            <div className="font-mono text-[11px] uppercase tracking-wider text-stone-400">{t(s.label)}</div>
            <div className={`font-serif text-2xl font-bold tabular-nums ${s.tone}`}>{s.value}</div>
          </div>
        ))}
      </div>

      <div className="space-y-4">
        {/* how a flag works */}
        <Section title="How a red flag works" testid="ref-how"
          sub="A flag is not derived from a score — it links one checklist tick to a clinical action.">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-lg border border-rose-300 bg-rose-50 p-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-rose-600" />
                <Chip tone="rose">{t("Urgent")}</Chip>
              </div>
              <p className="mt-2 text-sm text-rose-900">
                {t("Needs assessment or referral before the programme continues.")}
              </p>
            </div>
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <Chip tone="amber">{t("Discuss today")}</Chip>
              </div>
              <p className="mt-2 text-sm text-amber-900">
                {t("Must be raised at this consultation.")}
              </p>
            </div>
          </div>
          <p className="mt-3 flex items-start gap-1.5 text-xs text-stone-500">
            <ShieldCheck className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
            {t("Flags are matched by the exact checklist label, so renumbering a checklist can never point a flag at the wrong symptom.")}
          </p>
        </Section>

        <Section title="Urgent findings" testid="ref-urgent"
          tone="border-rose-200"
          sub="Assess or refer before the programme continues.">
          <FlagTable list={urgent} tier="urgent" />
        </Section>

        <Section title="Discuss today" testid="ref-caution"
          tone="border-amber-200"
          sub="Raise at this consultation.">
          <FlagTable list={caution} tier="caution" />
        </Section>

        {/* scoring logic */}
        <Section title="How a domain score is produced" testid="ref-domain-score"
          sub="Every scored worksheet has 8 statements.">
          <ul className="list-disc space-y-1.5 pl-5 text-sm text-stone-700">
            <li>{t("Each statement is answered 0–4 (or N/A).")}</li>
            <li>{t("A domain is scored only when all 8 statements are answered — otherwise it stays blank, so a gap is never read as a low score.")}</li>
            <li>{t("N/A answers are excluded from the denominator, so they don't drag the score down.")}</li>
            <li>
              {t("Score")} = <span className="font-mono">{t("points ÷ answerable × 10")}</span>, {t("rounded to one decimal, on a 0–10 scale.")}
            </li>
          </ul>
          <div className="mt-3">
            <div className="mb-2 font-mono text-[11px] uppercase tracking-wider text-stone-400">{t("Interpretation bands")}</div>
            <div className="grid gap-2 sm:grid-cols-3">
              <Band score={3} range="0 – 4.9" />
              <Band score={6} range="5 – 7.9" />
              <Band score={9} range="8 – 10" />
            </div>
          </div>
        </Section>

        <Section title="Overall & change" testid="ref-overall"
          sub="The headline figure on each snapshot.">
          <ul className="list-disc space-y-1.5 pl-5 text-sm text-stone-700">
            <li>{t("Overall is the mean of every scored domain in the current stage, rounded to one decimal.")}</li>
            <li>{t("The change arrow compares this stage's overall with the previous scored stage.")}</li>
            <li>
              {t("A per-domain drop of")} <b>{Math.abs(MATERIAL_DROP)}</b> {t("points or more between stages is treated as material and highlighted.")}
            </li>
          </ul>
        </Section>

        <Section title="Weakest domains" testid="ref-weakest"
          sub="Shown on the caseload row.">
          <p className="text-sm text-stone-700">
            {t("The three lowest-scoring completed domains for the participant, each coloured by the bands above. Unscored or partly answered domains are excluded so they cannot appear as weak.")}
          </p>
        </Section>

        <Section title="How the caseload is ranked" testid="ref-attention"
          sub="A triage ordering — not a clinical score.">
          <p className="mb-3 text-sm text-stone-700">
            {t("Each participant gets an attention rank from the sum of these weights. Higher means 'see sooner'.")}
          </p>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[520px] text-sm">
              <thead>
                <tr className="text-left font-mono text-[11px] uppercase tracking-wider text-stone-500">
                  <th className="px-3 py-2">{t("Signal")}</th>
                  <th className="px-3 py-2 w-24">{t("Weight")}</th>
                  <th className="px-3 py-2">{t("Note")}</th>
                </tr>
              </thead>
              <tbody>
                {weightRows.map((r) => (
                  <tr key={r.label} className="border-t border-[#EFE9DE] align-top">
                    <td className="px-3 py-2 font-medium text-stone-800">{t(r.label)}</td>
                    <td className="px-3 py-2 font-mono tabular-nums text-stone-700">{r.value}</td>
                    <td className="px-3 py-2 text-stone-600">{t(r.note)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        <Section title="Completeness" testid="ref-completeness"
          sub="Why the snapshot separates 'partial' from 'not started'.">
          <p className="text-sm text-stone-700">
            {t("A domain with some but not all 8 statements answered scores blank — identical on screen to one never started. The snapshot separates the two so gaps can be filled during the consultation rather than discovered at the end of the programme.")}
          </p>
        </Section>

        <Section title="Adherence" testid="ref-adherence"
          sub="Read on the same 0–10 scale as a domain.">
          <p className="mb-3 text-sm text-stone-700">
            {t("The 90-day plan is scored against each month's review, so a flat score reads as 'never attempted' rather than 'treatment failed'.")}
          </p>
          <div className="grid gap-2 sm:grid-cols-3">
            {adhBands.map((b) => (
              <div key={b.range} className="flex items-center gap-2 rounded-md border border-[#E5DFD3] bg-white px-3 py-2">
                <span className="h-3 w-3 flex-shrink-0 rounded-full" style={{ backgroundColor: b.tone.color }} />
                <span className="font-mono text-xs font-semibold tabular-nums text-stone-600">{b.range}</span>
                <span className="text-sm" style={{ color: b.tone.color }}>{t(b.tone.label)}</span>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Where the imbalance sits" testid="ref-groups"
          sub="The 28 scored domains, regrouped the way a vaidya reads a case.">
          <div className="grid gap-3 sm:grid-cols-2">
            {DOMAIN_GROUPS.map((g) => (
              <div key={g.key} className="rounded-lg border border-[#E5DFD3] bg-white p-3">
                <div className="font-serif text-base font-bold text-stone-900">{t(g.label)}</div>
                <div className="text-xs text-stone-500">{t(g.sub)}</div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {g.wids.map((wid) => (
                    <span key={wid} className="rounded-full border border-[#E5DFD3] px-2 py-0.5 text-[11px] text-stone-600">
                      <span className="font-mono text-stone-400">{t("W")}{getWorksheet(wid)?.num}</span>{" "}
                      {t(DOMAIN_LABEL[wid] || wid)}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-stone-500">
            {t("Every scored domain appears in exactly one group. Each group tile on a snapshot shows the average of its member domains.")}
          </p>
        </Section>
      </div>
    </AppShell>
  );
}
